"use client";

import { useCallback, useEffect, useState } from "react";

function readSeen(key: string) {
  try {
    return window.localStorage.getItem(key) === "seen";
  } catch {
    // Storage unavailable (private mode): show the hint each session.
    return false;
  }
}

function writeSeen(key: string) {
  try {
    window.localStorage.setItem(key, "seen");
  } catch {
    // The hint simply shows again next session.
  }
}

/**
 * A hint shown in the learner's first session only (remembered in this
 * browser). Within that session it stays until `dismiss()`; once it has been
 * shown, later sessions do not show it again.
 */
export function useOneTimeHint(key: string) {
  // Hidden until storage is read, so a learner who has seen it gets no flash.
  const [seenBefore, setSeenBefore] = useState(true);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    setSeenBefore(readSeen(key));
  }, [key]);

  const markShown = useCallback(() => writeSeen(key), [key]);
  const dismiss = useCallback(() => {
    setDismissed(true);
    writeSeen(key);
  }, [key]);

  return { available: !seenBefore && !dismissed, markShown, dismiss };
}
